import { type ComponentProps, type ReactNode, useId, useState } from 'react';
import { Upload } from 'lucide-react';

import { cn } from '@/util/utils';

type DropzoneProps = Omit<ComponentProps<'input'>, 'type' | 'onChange' | 'children'> & {
    label?: ReactNode;
    /** The accepted-types line, drawn inside the box under the label (2449:23826). */
    hint?: ReactNode;
    icon?: ReactNode;
    /** Called with the files chosen or dropped. */
    onFiles: (files: File[]) => void;
    className?: string;
};

/** "... or browse": the word you can act on, set apart the way the prototype's regex does. */
const BROWSE = /^(.*\s)(browse)$/i;

/**
 * ui-dropzone, Attachment / Dropzone 2449:23819: a dashed hairline box on the page background,
 * 10px corners, 22/20 padding and 6px between an 18px icon, the 12px label and the optional
 * hint, 126px minimum. A label ending in "browse" sets that word in primary medium. A real
 * file input sits behind it so keyboard and screen-reader users get the native picker;
 * dragging over highlights the box.
 */
function Dropzone({
    label = 'Drop file or click',
    hint,
    icon,
    onFiles,
    className,
    id,
    disabled,
    ...props
}: DropzoneProps) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const [over, setOver] = useState(false);

    return (
        <label
            htmlFor={inputId}
            data-slot="dropzone"
            data-dragover={over || undefined}
            className={cn(
                'bg-background border-border text-foreground relative flex min-h-[126px] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-5 py-[22px] text-center text-xs',
                'data-dragover:border-primary data-dragover:bg-muted has-focus-visible:outline-ring has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-solid',
                disabled && 'cursor-not-allowed opacity-50',
                className,
            )}
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(event) => {
                event.preventDefault();
                setOver(false);
                if (!disabled) onFiles(Array.from(event.dataTransfer.files));
            }}
        >
            <span aria-hidden className="[&_svg]:size-[18px]">
                {icon ?? <Upload />}
            </span>
            <span data-slot="dropzone-label">{withBrowse(label)}</span>
            {/* A space the flex column does not draw, so the label and hint that name the file
                input read as two phrases rather than "...browseJPG...". */}
            {hint ? ' ' : null}
            {hint ? (
                <span data-slot="dropzone-hint" className="text-label-sm text-muted-foreground">
                    {hint}
                </span>
            ) : null}
            <input
                id={inputId}
                type="file"
                className="sr-only"
                disabled={disabled}
                onChange={(event) => {
                    onFiles(Array.from(event.target.files ?? []));
                    event.target.value = '';
                }}
                {...props}
            />
        </label>
    );
}

function withBrowse(label: ReactNode): ReactNode {
    const match = typeof label === 'string' ? BROWSE.exec(label) : null;
    if (!match) return label;
    return (
        <>
            {match[1]}
            <span className="text-primary font-medium">{match[2]}</span>
        </>
    );
}

export { Dropzone, type DropzoneProps };
